// screens/tenant-delete-verdict.ts — CE QU'UN REFUS DE SUPPRESSION DE CLIENT VEUT DIRE.
//
// 🔴 LE DÉFAUT QUI A MOTIVÉ CE FICHIER. La route `DELETE /annuaire/tenants/:id` répond 502 dans QUATRE
// situations, et l'une seule signifie « rien n'a été touché ». Les trois autres veulent dire « le client
// est SUPPRIMÉ, voici ce qui subsiste ». L'écran les traitait toutes comme un échec total : il titrait
// « Suppression impossible » et ne rafraîchissait même pas la liste — donc l'opérateur lisait « impossible »
// sur un client déjà détruit, et devait DÉCOUVRIR par lui-même que la base n'existait plus.
//
// ⚠️ LE TITRE EST CE QU'ON LIT EN PREMIER, et le corps disait pourtant la vérité (« Client et bases
// supprimés, mais les images… »). Un cadre qui démentit son propre contenu est pire qu'un message vague :
// on croit le cadre (§23.0).
//
// ⚠️ POURQUOI LA CLASSIFICATION VIT ICI ET PAS DANS CHAQUE APPELANT : c'est le serveur qui DÉCIDE, l'écran
// ne fait que lire. Une condition réénumérée à l'endroit où on l'affiche finit par diverger du jour où le
// serveur ajoute un code (Forme FN) — d'où la garde qui compare cette liste aux codes RÉELS de la route,
// lus au parseur, et qui refuse un code non classé.
//
// ⚠️ ET LE DÉFAUT PAR DÉFAUT EST « JE NE SAIS PAS », JAMAIS « RIEN N'A ÉTÉ TOUCHÉ » : c'est précisément ce
// dernier repli qui a produit l'incident. Un code inconnu rend `unknown`, l'écran le dit, et rafraîchit —
// parce qu'entre « peut-être détruit » et « sûrement intact », seule la première lecture est prudente.

/** Ce que le refus dit de l'état réel du client. */
export type TenantDeleteVerdict =
  /** Le geste a été refusé AVANT toute destruction — le client est intact, on peut réessayer. */
  | 'untouched'
  /** Le client est bel et bien SUPPRIMÉ ; il reste des résidus à rattraper ailleurs. */
  | 'deleted'
  /** Code non classé : on ne sait pas, et on le dit plutôt que de parier. */
  | 'unknown';

/**
 * Les codes que la route émet, et ce qu'ils disent de l'état du client.
 *
 * ⚠️ `access_revoke_failed` est le plus GRAVE des trois « supprimé » — une clé d'API qui vit encore est un
 * accès ouvert sur un magasin que l'annuaire ne connaît plus — mais il reste un « supprimé » : le client
 * n'existe plus, l'écran doit donc revenir à la liste.
 */
const VERDICTS: Record<string, TenantDeleteVerdict> = {
  not_found: 'untouched',
  registry_delete_failed: 'untouched',
  access_revoke_failed: 'deleted',
  stores_dropped_partially: 'deleted',
  media_purged_partially: 'deleted',
};

/** Les codes classés — lus par la garde qui les confronte à la route. */
export const CLASSIFIED_TENANT_DELETE_CODES = Object.keys(VERDICTS);

/** Le verdict porté par un code de refus. Un code inconnu rend `unknown`, jamais un repli rassurant. */
export function classifyTenantDelete(code: string | undefined): TenantDeleteVerdict {
  if (!code) return 'unknown';
  return VERDICTS[code] ?? 'unknown';
}

/** Le titre de l'alerte — il doit dire l'ÉTAT, pas seulement qu'il y a eu un problème. */
export function tenantDeleteTitle(verdict: TenantDeleteVerdict, name: string): string {
  if (verdict === 'untouched') return `Suppression impossible — ${name} est intact`;
  if (verdict === 'deleted') return `${name} est SUPPRIMÉ, mais il reste des résidus`;
  return `${name} : état incertain après la suppression`;
}
